<template>
  <USelectMenu
    ref="selectmenu"
    v-bind="$props"
    v-model="option"
    size="md"
    :searchable="searchableRef"
    :searchable-placeholder="$t('components.forms.ASelectMenu.placeholder')"
    :debounce
    :value-attribute="undefined"
    :by="valueAttribute"
    @click="handleClickOnSelectMenu"
    @update:model-value="onUpdate"
  >
    <template #option-empty="{ query }">
      {{ $t("components.forms.ASelectMenu.noResults.query", { query }) }}
    </template>
    <template #empty>
      {{ $t("components.forms.ASelectMenu.noResults") }}
    </template>
    <template v-for="(_, name) in $slots" #[name]="slotData">
      <slot :name="name" v-bind="slotData" />
    </template>
  </USelectMenu>
</template>

<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    guardedAttributes?: string[]
    hackHeadlessUi?: boolean
    optionAttribute?: string
    options?: any[]
    placeholder?: string
    searchable?:
      | ((search: string) => Promise<any[]>)
      | boolean
      | Extract<keyof Bff, `${string}/select`>
    searchableParams?: Record<string, any>
    valueAttribute?: string
  }>(),
  {
    guardedAttributes: () => [],
    hackHeadlessUi: false, // https://github.com/nuxt/ui/issues/1708 has to be deleted when upgrading to v3 version of Nuxt UI
    optionAttribute: 'name',
    searchable: false,
    searchableParams: () => ({}),
    valueAttribute: 'id',
  },
)

const emits = defineEmits<{
  'reset': any
  'update:value': any
}>()
const selectedOption = shallowRef<any>(null)
const selectmenu = ref<{ $el: HTMLElement } | null>(null)
const option = defineModel<any>('option')
const model = defineModel<number | string | undefined>()

if (model.value === null) {
  model.value = undefined
}

const searchableRef = ref(props.searchable as any)
const selectOptionsWasHovered = ref(!props.hackHeadlessUi) // https://github.com/nuxt/ui/issues/1708 has to be deleted when upgrading to v3 version of Nuxt UI

if (typeof searchableRef.value === 'string') {
  const searchableEndpoint = searchableRef.value
  searchableRef.value = async (search: string): Promise<any> => {
    return await $fetch(searchableEndpoint, {
      params: {
        search,
        ...props.searchableParams,
      },
    })
  }
}

if (searchableRef.value === true && props.options?.length) {
  searchableRef.value = (term: string) => {
    if (!term) {
      return props.options
    }

    return props.options?.filter(option =>
      option[props.optionAttribute]
        .toLowerCase()
        .replace(/[éèêë]/g, 'e')
        .replace(/[àâä]/g, 'a')
        .replace(/[ìîï]/g, 'i')
        .replace(/[òôö]/g, 'o')
        .replace(/[ùûü]/g, 'u')
        .startsWith(term.toLowerCase()),
    )
  }
}

async function refreshSearchableList() {
  const initialSearchFn = searchableRef.value
  searchableRef.value = false
  await nextTick(async () => {
    searchableRef.value = initialSearchFn
  })
}

function reset() {
  selectedOption.value = null
  option.value = undefined
  emits('reset')
}

function selectOption(data: any) {
  option.value = data
  model.value = data[props.valueAttribute]
}

const debounce = process.env.TEST ? 10 : 400

function onUpdate(value: any) {
  if (!selectOptionsWasHovered.value && props.hackHeadlessUi) {
    reset()
    return
  }

  selectedOption.value = value
  option.value = Object.fromEntries(
    Object.entries(value).filter(([key]) =>
      [
        props.optionAttribute,
        props.valueAttribute,
        ...props.guardedAttributes,
      ].includes(key),
    ),
  )
  model.value = value[props.valueAttribute]
  emits('update:value', value)
  selectOptionsWasHovered.value = false
}

defineExpose({
  refreshSearchableList,
  reset,
  selectedOption,
  selectmenu,
  selectOption,
})

function handleClickOnSelectMenu(event: Event) {
  if (!(event.currentTarget instanceof HTMLElement) || !props.hackHeadlessUi)
    return

  const parentId = event.currentTarget.parentElement?.getAttribute('id')
  setTimeout(() => {
    const optionsContainer = document.querySelector(
      `[aria-labelledby="${parentId}"]`,
    )

    if (!optionsContainer)
      return

    const options = optionsContainer.querySelectorAll('li[role="option"]')
    if (options) {
      Array.from(options).forEach((option) => {
        option.addEventListener('mouseleave', () => {
          selectOptionsWasHovered.value = false
        })
        option.addEventListener('mouseover', () => {
          selectOptionsWasHovered.value = true
        })
        option.addEventListener('touchstart', () => {
          selectOptionsWasHovered.value = true
        })
      })
    }
  }, 200)
}

onMounted(() => {
  const options = props.options?.filter(Boolean)
  if (options?.length) {
    option.value = options.find(
      (o: any) =>
        o[props.valueAttribute]?.toString() === model.value?.toString(),
    )
  }

  option.value ??= model.value
})
</script>
